/**
 * GoogleOneTap — Widget Google One-Tap (sign-in en 1 clic).
 *
 * Affiche le prompt natif Google en haut-droite de la page si :
 *  - L'utilisateur n'est PAS connecté à Peter's Universe
 *  - Le flow direct est activé côté backend (`googleConfig.enabled`)
 *  - L'utilisateur n'a pas dismissé le widget dans le passé (LocalStorage)
 *  - L'utilisateur a un compte Google actif dans son navigateur
 *
 * Flow technique :
 *   Google renvoie un JWT (id_token) signé → on l'envoie au backend qui
 *   le décode et upserte le user. Bien plus direct que le flow auth-code
 *   (pas besoin d'un échange code/token côté backend).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { GoogleOAuthProvider, useGoogleOneTapLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ONE_TAP_DISMISSED_KEY = 'gsi_one_tap_dismissed_at';
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

const InnerOneTap = ({ next, onSuccess }) => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleCredential = useCallback(async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      // eslint-disable-next-line no-console
      console.warn('[one-tap] no credential in response');
      return;
    }
    try {
      const r = await axios.post(
        `${API}/auth/google/onetap`,
        { id_token: idToken },
        { withCredentials: true }
      );
      await checkAuth();
      toast.success(`Bienvenue ${r.data?.user?.name || ''}`.trim());
      if (onSuccess) onSuccess(r.data);
      else navigate(next || '/hub/addresses');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Échec de la connexion en 1 clic';
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error('[one-tap] exchange failed', e);
    }
  }, [checkAuth, navigate, next, onSuccess]);

  useGoogleOneTapLogin({
    onSuccess: handleCredential,
    onError: () => {
      // Si l'utilisateur ferme le prompt manuellement, on note la date pour ne pas le ré-afficher tout de suite
      try { localStorage.setItem(ONE_TAP_DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    },
    cancel_on_tap_outside: false,
    auto_select: false,         // ne pas signer automatiquement
    use_fedcm_for_prompt: true, // FedCM ready (mandatory dès 2025 sur Chrome)
  });

  return null;
};

const GoogleOneTap = ({ next, onSuccess }) => {
  const { user, googleConfig } = useAuth();
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Vérifie le délai de dismiss
    let dismissed = false;
    try {
      const raw = localStorage.getItem(ONE_TAP_DISMISSED_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!Number.isNaN(ts) && Date.now() - ts < DISMISS_WINDOW_MS) {
          dismissed = true;
        }
      }
    } catch { /* ignore */ }
    setShouldShow(!dismissed && !user && !!googleConfig?.enabled && !!googleConfig?.client_id);
  }, [user, googleConfig]);

  if (!shouldShow) return null;

  return (
    <GoogleOAuthProvider clientId={googleConfig.client_id}>
      <InnerOneTap next={next} onSuccess={onSuccess} />
    </GoogleOAuthProvider>
  );
};

export default GoogleOneTap;
